// src/components/dashboard/DashboardLayout.js - FIXED CalendarView with Real Booking Data
import {
  Bell,
  Calendar,
  Home,
  Plus,
  Settings,
  TrendingUp,
  Users,
  X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useBusiness } from '../../hooks/useBusiness';
import { useConfig } from '../ConfigProvider';

// Import all dashboard components
import BookingAnalytics from './BookingAnalytics';
import BookingManager from './BookingManager';
import BusinessSettings from './BusinessSettings';
import CustomerManager from './CustomerManager';
import DashboardOverview from './DashboardOverview';
import HolidayCalendar from './HolidayCalendar';
import QuickBookingWidget from './QuickBookingWidget';
import ServiceManager from './ServiceManager';
import VoiceAndSIPSetup from '../../pages/VoiceAndSIPSetup';

const DashboardLayout = () => {
  const { user } = useAuth();
  const { businessProfile } = useBusiness();
  const { features: featureFlags } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isSipSetupEnabled = Boolean(featureFlags?.sipSetup);

  const deriveTabFromLocation = () => {
    const searchParams = new URLSearchParams(location.search);
    let tab = searchParams.get('tab') || 'overview';

    if (location.pathname === '/settings/voice-and-sip' || tab === 'voice-sip') {
      tab = isSipSetupEnabled ? 'settings-voice' : 'overview';
    }

    return tab;
  };

  const [activeTab, setActiveTab] = useState(deriveTabFromLocation);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showQuickBooking, setShowQuickBooking] = useState(false);
  const [notifications] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null); // ADDED: For booking detail modal
  
  // ==========================================
  // FIXED: Stable date management for CalendarView
  // ==========================================
  
  // Create a stable selected date that only changes when explicitly set
  const [calendarSelectedDate] = useState(() => new Date());
  
  // Memoize the selected date to prevent unnecessary re-renders
  const stableSelectedDate = useMemo(() => calendarSelectedDate, [calendarSelectedDate]);

  useEffect(() => {
    const nextTab = deriveTabFromLocation();
    setActiveTab(nextTab);
  }, [location.pathname, location.search, isSipSetupEnabled]);

  const navigationItems = useMemo(() => {
    return [
      {
        id: 'overview',
        name: 'Home',
        icon: Home,
        component: DashboardOverview,
        description: 'Business dashboard and quick actions'
      },
      {
        id: 'bookings',
        name: 'Bookings',
        icon: Calendar,
        component: BookingManager,
        description: 'Manage appointments and schedules'
      },
      {
        id: 'customers',
        name: 'Customers',
        icon: Users,
        component: CustomerManager,
        description: 'Customer relationships and insights'
      },
      {
        id: 'analytics',
        name: 'Reports',
        icon: TrendingUp,
        component: BookingAnalytics,
        description: 'Business performance and insights'
      },
      {
        id: 'settings',
        name: 'Settings',
        icon: Settings,
        component: BusinessSettings,
        description: 'Business profile and preferences'
      }
    ];
  }, []);

  // Find the active navigation item
  const isActiveNavigationItem = (itemId) => {
    if (itemId === 'settings') {
      return activeTab === 'settings' || activeTab.startsWith('settings-');
    }

    return activeTab === itemId;
  };

  const activeItem = navigationItems.find(item => isActiveNavigationItem(item.id)) || navigationItems[0];

  const handleNavigation = (itemId) => {
    const nextTab = itemId === 'settings' ? 'settings-business' : itemId;

    if (nextTab === 'overview') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard?tab=${nextTab}`);
    }
    
    setActiveTab(nextTab);
  };
  
  // ==========================================
  // FIXED: Component rendering with proper data
  // ==========================================
  
  // Memoize component rendering to prevent unnecessary re-renders
  const renderActiveComponent = useCallback(() => {
    if (activeTab === 'overview') {
      return (
        <DashboardOverview
          onNavigateToBookings={() => setActiveTab('bookings')}
          onNavigateToCustomers={() => setActiveTab('customers')}
          onNavigateToCalendarView={() => setActiveTab('bookings')}
          onCreateBooking={() => setShowQuickBooking(true)}
        />
      );
    }

    if (activeTab === 'bookings') return <BookingManager />;
    if (activeTab === 'customers') return <CustomerManager />;
    if (activeTab === 'analytics') return <BookingAnalytics />;

    if (activeTab === 'settings' || activeTab.startsWith('settings-')) {
      const settingsSections = [
        { id: 'settings-business', label: 'Business' },
        { id: 'settings-services', label: 'Services' },
        { id: 'settings-calendar', label: 'Calendar & Closures' },
        { id: 'settings-voice', label: 'Voice Setup' }
      ];

      return (
        <div>
          <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
            {settingsSections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveTab(section.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === section.id || (activeTab === 'settings' && section.id === 'settings-business')
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>

          {(activeTab === 'settings' || activeTab === 'settings-business') && <BusinessSettings />}
          {activeTab === 'settings-services' && <ServiceManager />}
          {activeTab === 'settings-calendar' && <HolidayCalendar />}
          {activeTab === 'settings-voice' && <VoiceAndSIPSetup />}
        </div>
      );
    }

    return <DashboardOverview
      onNavigateToBookings={() => setActiveTab('bookings')}
      onNavigateToCustomers={() => setActiveTab('customers')}
      onNavigateToCalendarView={() => setActiveTab('bookings')}
      onCreateBooking={() => setShowQuickBooking(true)}
    />;
  }, [
    activeTab
  ]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-sm border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* Logo Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            {sidebarOpen && (
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">
                  {businessProfile?.businessName || 'BookingPro'}
                </h1>
                <p className="text-sm text-gray-500">Dashboard</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-4 space-y-2">
          {navigationItems.filter(item => item.enabled !== false).map((item) => {
            const Icon = item.icon;
            const isActive = isActiveNavigationItem(item.id);
            
            return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.id)}
              className={`
                  w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
                title={item.description}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                {sidebarOpen && (
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{item.name}</span>
                      {item.badge && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    {sidebarOpen && !isActive && (
                      <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Toggle */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3 text-sm">Collapse</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {activeItem?.name || 'Dashboard'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {activeItem?.description || 'Manage your business'}
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Quick Actions */}
              <button
                onClick={() => setShowQuickBooking(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Booking
              </button>

              {/* Notifications */}
              <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </button>

              {/* User Profile */}
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-700">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.email || 'User'}
                  </p>
                  <p className="text-xs text-gray-500">Admin</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          {renderActiveComponent()}
        </main>
      </div>

      {/* Quick Booking Modal */}
      {showQuickBooking && (
        <QuickBookingWidget 
          isOpen={showQuickBooking}
          onClose={() => setShowQuickBooking(false)}
          preselectedDate={stableSelectedDate}
        />
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <BookingDetailModal 
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onEdit={() => {
            // Navigate to bookings tab with this booking selected
            setActiveTab('bookings');
            setSelectedBooking(null);
          }}
        />
      )}
    </div>
  );
};

// Booking Detail Modal Component
const BookingDetailModal = ({ booking, onClose, onEdit }) => {
  // FIXED: Handle the actual data structure being passed
  console.log('📋 Modal received booking data:', booking);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date not available';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'Time not available';
    const [hours, minutes] = timeStr.split(':');
    const time = new Date();
    time.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rescheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // FIXED: Extract data from the actual structure being passed
  // Handle both nested (booking.booking) and flat (booking) structures
  const bookingData = booking.booking || booking;
  const eventData = booking; // Event data from calendar
  
  // FIXED: Get service information
  const serviceName = bookingData.serviceName || bookingData.service_name || eventData.title?.split(' - ')[0] || 'Service';
  const servicePrice = bookingData.servicePrice || bookingData.service_price;
  
  // FIXED: Get customer information  
  const customerName = bookingData.customerName || bookingData.customer_name || eventData.title?.split(' - ')[1] || 'Customer';
  const customerPhone = bookingData.customerPhone || bookingData.customer_whatsapp || bookingData.customer_phone;
  const customerEmail = bookingData.customerEmail || bookingData.customer_email;
  
  // FIXED: Get date/time information
  const bookingDate = bookingData.bookingDate || bookingData.booking_date || eventData.start?.toISOString().split('T')[0];
  const bookingTime = bookingData.time || bookingData.booking_time || 
                     (eventData.start ? eventData.start.toTimeString().slice(0, 5) : null);
  const duration = bookingData.duration || bookingData.duration_minutes;
  
  // FIXED: Get other information
  const status = bookingData.status;
  const notes = bookingData.notes;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Booking Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Service Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Service</h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium text-gray-900">{serviceName}</p>
              {servicePrice && (
                <p className="text-sm text-gray-600">${servicePrice}</p>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Customer</h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium text-gray-900">{customerName}</p>
              {customerPhone && (
                <p className="text-sm text-gray-600">{customerPhone}</p>
              )}
              {customerEmail && (
                <p className="text-sm text-gray-600">{customerEmail}</p>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Date & Time</h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium text-gray-900">
                {formatDate(bookingDate)}
              </p>
              <p className="text-sm text-gray-600">
                {formatTime(bookingTime)}
              </p>
              {duration && (
                <p className="text-sm text-gray-600">{duration} minutes</p>
              )}
            </div>
          </div>

          {/* Status */}
          {status && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-700">{notes}</p>
              </div>
            </div>
          )}

          {/* Booking ID (for debugging) */}
          {bookingData.bookingId && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Booking ID</h3>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 font-mono">{bookingData.bookingId}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex space-x-3">
          <button
            onClick={onEdit}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Booking
          </button>
          
          {customerPhone && (
            <button
              onClick={() => {
                const phoneNumber = customerPhone.replace(/\D/g, '');
                const message = `Hi ${customerName}, this is regarding your ${serviceName} scheduled for ${formatDate(bookingDate)} at ${formatTime(bookingTime)}.`;
                window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
              }}
              className="px-4 py-2 text-green-600 border border-green-600 rounded-lg hover:bg-green-50 transition-colors"
            >
              WhatsApp
            </button>
          )}
          
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
